/*
 * dunkleycustom — Map (Google): standalone port of Expanse 2.1.1's theme.Maps
 *
 * Drives each [data-luma-google-map] node rendered by sections/map-google.liquid:
 *   1. Lazily injects the Google Maps JS API (once, shared across instances)
 *      when the section nears the viewport (IntersectionObserver, mirroring
 *      2.1.1's theme.initWhenVisible).
 *   2. Geocodes data-address, then renders a locked-down map (no UI, not
 *      draggable, no clickable POIs) with a marker, re-centering on resize.
 *   3. Errors (bad key, bad address, quota) show a message ONLY in the theme
 *      editor (Shopify.designMode) — on the storefront failures stay silent
 *      and the section's Liquid fallback stays visible, exactly like 2.1.1.
 *
 * NOTE: Google's console logs deprecation warnings for this loading style and
 * for google.maps.Marker. They are cosmetic — deliberate parity with the
 * proven live 2.1.1 implementation; do not "fix" them from the console.
 */
(function () {
  if (window.__lumaMapGoogleLoaded) return;
  window.__lumaMapGoogleLoaded = true;

  var apiPromise = null;
  var authErrorHtml = '';

  function inDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function debounce(wait, fn) {
    var timeout;
    return function () {
      var self = this;
      var args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function loadApi(key) {
    if (window.google && window.google.maps) return Promise.resolve();
    if (!apiPromise) {
      apiPromise = new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        script.onload = resolve;
        script.onerror = reject;
        script.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key);
        document.head.appendChild(script);
      });
    }
    return apiPromise;
  }

  function geocode(address) {
    return new Promise(function (resolve, reject) {
      var geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: address }, function (results, status) {
        if (status !== google.maps.GeocoderStatus.OK) {
          reject(status);
          return;
        }
        resolve(results);
      });
    });
  }

  function showError(html) {
    if (!html) return;
    document.querySelectorAll('.map-google-section').forEach(function (sectionEl) {
      if (!sectionEl.querySelector('.map-google__error')) {
        var error = document.createElement('div');
        error.classList.add('map-google__error');
        error.innerHTML = html;
        var content = sectionEl.querySelector('.content-over-media__content');
        (content || sectionEl).prepend(error);
      }
      var link = sectionEl.querySelector('.map-google__link');
      if (link) link.style.display = 'none';
    });
  }

  // Google calls this global when the key fails auth. 2.1.1 parity: only
  // surface it in the theme editor; shoppers keep the Liquid fallback.
  window.gm_authFailure = function () {
    if (!inDesignMode()) return;
    document.querySelectorAll('.map-google-section').forEach(function (sectionEl) {
      sectionEl.classList.add('map-google-section--load-error');
      sectionEl.querySelectorAll('[data-luma-google-map]').forEach(function (el) {
        el.remove();
      });
    });
    showError(authErrorHtml);
  };

  function createMap(el) {
    loadApi(el.dataset.apiKey)
      .then(function () {
        return geocode(el.dataset.address);
      })
      .then(function (results) {
        var map = new google.maps.Map(el, {
          zoom: 14,
          backgroundColor: 'none',
          center: results[0].geometry.location,
          draggable: false,
          clickableIcons: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          disableDefaultUI: true
        });
        new google.maps.Marker({ map: map, position: map.getCenter() });

        var center = map.getCenter();
        var onResize = debounce(250, function () {
          if (!document.body.contains(el)) {
            window.removeEventListener('resize', onResize);
            return;
          }
          google.maps.event.trigger(map, 'resize');
          map.setCenter(center);
          el.removeAttribute('style');
        });
        window.addEventListener('resize', onResize);
        el.__lumaMapCleanup = function () {
          window.removeEventListener('resize', onResize);
        };
      })
      .catch(function (status) {
        if (!inDesignMode()) return;
        var messages = {
          ZERO_RESULTS: el.dataset.errorNoResults,
          OVER_QUERY_LIMIT: el.dataset.errorQueryLimit,
          REQUEST_DENIED: el.dataset.errorAuth
        };
        showError(messages[status] || el.dataset.errorAddress || '');
      });
  }

  function initEl(el) {
    if (el.__lumaMapInit) return;
    el.__lumaMapInit = true;
    if (!el.dataset.apiKey || !el.dataset.address) return;
    authErrorHtml = el.dataset.errorAuth || authErrorHtml;

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              obs.unobserve(entry.target);
              createMap(el);
            }
          });
        },
        { rootMargin: '0px 0px 20px 0px' }
      );
      observer.observe(el);
    } else {
      createMap(el);
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-luma-google-map]').forEach(initEl);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme-editor lifecycle (replaces 2.1.1's theme.sections.register).
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-luma-google-map]').forEach(function (el) {
      if (el.__lumaMapCleanup) el.__lumaMapCleanup();
    });
  });
})();
